import { SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Col, Input, Row, Space } from 'antd';
import { useEffect, useState } from 'react';
import { getZones } from '../../api';
import { components, paths } from '../../api/schema';
import DataTable, {
  ColumnsType,
  DataTableFilterCallback,
  DataTableOrderCallback,
  serializeFilters,
  serializeOrders,
} from '../../shared/dataTable/DataTable';
import useDebounce from '../../shared/hooks/useDebounce';
import PanelSkeleton from '../../shared/skeletons/PanelSkeleton';
import CreateZone from './CreateZone';
import { DeleteZone } from './zonePanelActions/DeleteAction';

const zonesTableColumns: ColumnsType<components['schemas']['ZoneCondensed']> = [
  {
    title: 'Nombre',
    dataIndex: 'name',
    key: 'name',
    columnType: 'text',
  },
  {
    title: 'Tipo',
    dataIndex: 'zoneType',
    key: 'zoneType',
    columnType: 'enum',
  },
  {
    title: 'Distancia',
    dataIndex: 'distance',
    key: 'distance',
    columnType: 'number',
  },
  {
    title: 'Precio entrega/recolección',
    dataIndex: 'pickupDeliveryPrice',
    key: 'pickupDeliveryPrice',
    columnType: 'number',
  },
  {
    title: 'Horario recolección',
    dataIndex: 'pickupBefore',
    key: 'pickupBefore',
    columnType: 'number',
  },
  {
    title: 'Leyenda a mostrar',
    dataIndex: 'daysAvailable',
    key: 'daysAvailable',
    columnType: 'text',
  },
];
const PAGE_SIZE = 50;

export default function ZonePanel() {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce<string>(search, 300);
  const [params, setParams] = useState<paths['/zone/list']['get']['parameters']['query']>({
    search: undefined,
    limit: PAGE_SIZE,
    page: 1,
    showDisabled: false,
    order: JSON.stringify([]),
    filter: JSON.stringify([]),
  });
  const { isLoading, isError, data, isFetching } = useQuery({
    queryKey: ['getZones', params],
    queryFn: () => getZones(params),
  });

  useEffect(() => {
    setParams((state) => ({ ...state, search: debouncedSearch }));
  }, [debouncedSearch]);

  const handleFiltering = (filter: DataTableFilterCallback) => {
    setParams((state) => ({ ...state, filter: serializeFilters(params.filter, filter) }));
  };

  const handleOrdering = (order: DataTableOrderCallback) => {
    setParams((state) => ({ ...state, order: serializeOrders(params.order, order) }));
  };

  const handlePageChange = (page: number) => {
    setParams((state) => ({ ...state, page }));
  };

  return (
    <Row>
      <Col span={24}>
        <Space size={'middle'}>
          <Button type="primary" onClick={() => setCreateModalOpen(true)}>
            Crear Zona
          </Button>
          <Input
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            suffix={<SearchOutlined />}
          />
        </Space>
      </Col>
      {isLoading || isError || isFetching ? (
        <PanelSkeleton />
      ) : (
        <>
          <Col span={24} style={{ marginTop: '10px' }}>
            <DataTable<components['schemas']['ZoneCondensed']>
              handleFilters={handleFiltering}
              handleOrder={handleOrdering}
              handlePageChange={handlePageChange}
              columns={zonesTableColumns}
              results={data.results}
              activeFilters={params.filter}
              activeOrders={params.order}
              paginationData={{
                pageSize: PAGE_SIZE,
                count: data.count,
                currentPage: data.currentPage,
              }}
              actions={[DeleteZone]}
            />
          </Col>

          <CreateZone
            open={createModalOpen}
            onCancel={() => setCreateModalOpen(false)}
            onCreate={() => setCreateModalOpen(false)}
          />
        </>
      )}
    </Row>
  );
}
